// NOTE: Financial fields from MonthlyCompensationSerializer are nested { total, breakdown }.
// All columns use r.field?.total for the value and r.field?.breakdown for the slots.
import { Button, DdsDrawCell, BreakdownCell, Tooltip } from "@common"; // BreakdownCell/Tooltip for hover tooltips
import { Link } from "react-router-dom";
import { formatMoney } from "./format";
import {
  hasFeeConfigured,
  isApprovalApproved,
  computeTotalDeductions,
  TotalFeeHeader,
} from "./associateFee";
import { validateCheckData } from "./netsuiteCheck";
import {
  LINK_CLASSES,
  TYPE_PILL_COLORS,
  DDS_DRAW_COLORS,
  PROFIT_COLORS,
  OTHER_PROFIT_COLORS,
  TOTAL_FEE_COLORS,
  DEDUCTION_COLORS,
  FEE_PERCENT_COLORS,
  CHECK_HEADER_COLOR,
} from "./columnColors";
import { isClearedBefore15th } from "./checkDateUtils";

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const percent = (n) => {
  if (n === null || n === undefined || n === "") return "-";
  const v = Number(n);
  if (Number.isNaN(v)) return "-";
  return `${v.toFixed(2)}%`;
};

const textOrDash = (v) => (v != null && String(v).trim() !== "" ? v : "-");

const hasFee = hasFeeConfigured;

const ExpectedClearHover = ({ label, clearedDates = [] }) => (
  <Tooltip
    variant="light"
    align="start"
    interactive
    width={288}
    triggerAs="button"
    triggerProps={{ type: "button", title: "Hover to view recent cleared dates" }}
    triggerClassName="text-[11px] text-palette-slate-badge underline decoration-dotted underline-offset-2 cursor-help"
    content={
      <>
        <div className="mb-1 font-semibold text-palette-slate-text">Recent cleared dates</div>
        {clearedDates.length > 0 ? (
          <ul className="space-y-0.5">
            {clearedDates.map((dt, idx) => (
              <li key={`${dt}-${idx}`}>{dt}</li>
            ))}
          </ul>
        ) : (
          <div className="text-palette-slate-secondary">No cleared dates</div>
        )}
      </>
    }
  >
    {label}
  </Tooltip>
);

/**
 * Columns for the Check tab. Starts as a copy of journalEntryColumns so it
 * can diverge independently (add/remove/reorder columns without affecting
 * the Journal Entry tab).
 */
export const checkColumns = (
  onUpdateClick,
  {
    onApprove,
    onCreateCheck,
    onPractitionerClick,
    findExistingChecksForRow,
    justCreatedKeys,
    expectedClearInfoByVendor,
    expectedClearLoading = false,
    deductionsExpanded = false,
    onToggleDeductions,
  } = {},
) => [
  {
    key: "clinic",
    header: "CLINIC",
    width: 100,
    render: (r) => {
      const code = r.clinic;
      if (!code) return textOrDash(code);
      const monthParam = /^\d{4}-\d{2}/.test(String(r.month || ""))
        ? String(r.month).slice(0, 7)
        : "";
      const to = `/report/${encodeURIComponent(code)}?tab=monthly${
        monthParam ? `&month=${monthParam}` : ""
      }`;
      return (
        <Link to={to} title={`Open ${code} monthly reconciliation`} className={LINK_CLASSES}>
          {code}
        </Link>
      );
    },
  },
  {
    key: "practitioner_name",
    header: "PRACTITIONER",
    width: 140,
    render: (r) => {
      const name = textOrDash(r.practitioner_name);
      if (!onPractitionerClick || name === "-" || !r.practitioner_id) return name;
      return (
        <button
          type="button"
          onClick={() => onPractitionerClick(r)}
          title="View practitioner profile"
          className={LINK_CLASSES}
        >
          {name}
        </button>
      );
    },
  },
  {
    key: "practitioner_type",
    header: "TYPE",
    width: 70,
    render: (r) => {
      const t = (r.practitioner_type || "").toString().toUpperCase();
      if (!t) return <span className="text-palette-gray-placeholder text-xs">-</span>;
      const color = TYPE_PILL_COLORS[t] || TYPE_PILL_COLORS.default;
      return (
        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${color}`}>
          {t}
        </span>
      );
    },
  },
  {
    key: "dds_draw",
    header: "DDS DRAW",
    width: 120,
    headerClassName: DDS_DRAW_COLORS.header,
    cellClassName: DDS_DRAW_COLORS.cell,
    render: (r) => {
      const isHyg = String(r.practitioner_type || "").toUpperCase() === "HYG";
      if (!hasFee(r)) {
        if (isHyg) {
          return (
            <span className="text-palette-gray-placeholder text-xs italic">
              Not applicable (HYG)
            </span>
          );
        }
        return <span className="text-palette-gray-placeholder text-xs italic">Set fee %</span>;
      }
      return <DdsDrawCell row={r} compact />;
    },
  },
  {
    key: "production",
    header: "PRODUCTION",
    width: 100,
    headerClassName: PROFIT_COLORS.header,
    cellClassName: PROFIT_COLORS.cell,
    render: (r) =>
      toNum(r.production?.total) === 0 ? (
        <span className="text-palette-sky-heading/70 text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.production?.total}
          breakdown={r.production?.breakdown}
          label="Production"
        />
      ),
  },
  {
    key: "collection",
    header: "COLLECTION",
    width: 100,
    headerClassName: PROFIT_COLORS.header,
    cellClassName: PROFIT_COLORS.cell,
    render: (r) =>
      toNum(r.collection?.total) === 0 ? (
        <span className="text-palette-sky-heading/70 text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.collection?.total}
          breakdown={r.collection?.breakdown}
          label="Collection"
        />
      ),
  },
  {
    key: "other_profit",
    header: "OTHER PROFIT",
    width: 110,
    headerClassName: OTHER_PROFIT_COLORS.header,
    cellClassName: OTHER_PROFIT_COLORS.cell,
    render: (r) => {
      const tiers = Array.isArray(r.other_profit) ? r.other_profit : [];
      if (tiers.length === 0)
        return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      const total = tiers.reduce((sum, t) => sum + toNum(t?.profit), 0);
      const title = tiers
        .map((t) => {
          const pct = Number(t?.percent) || 0;
          const input = toNum(t?.input_value);
          const profit = toNum(t?.profit);
          return `${t?.label || "?"} (${pct}%): ${formatMoney(input)} → ${formatMoney(profit)}`;
        })
        .join("\n");
      return (
        <span className="text-palette-sky-primary cursor-help" title={title}>
          {formatMoney(total)}
        </span>
      );
    },
  },
  {
    key: "total_fee",
    header: <TotalFeeHeader expanded={deductionsExpanded} onToggle={onToggleDeductions} />,
    width: 110,
    align: "center",
    headerClassName: TOTAL_FEE_COLORS.header,
    cellClassName: TOTAL_FEE_COLORS.cell,
    render: (r) => {
      const total = computeTotalDeductions(r);
      if (total === 0) return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      return <span className="font-semibold text-palette-rose-primary">{formatMoney(total)}</span>;
    },
  },
  {
    key: "lab_cost",
    header: "LAB COSTS",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.lab_cost?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.lab_cost?.total}
          breakdown={r.lab_cost?.breakdown}
          label="Lab Costs"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "material_cost",
    header: "MATERIAL",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.material_cost?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.material_cost?.total}
          breakdown={r.material_cost?.breakdown}
          label="Material"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "invisalign_cost",
    header: "INVISALIGN",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.invisalign_cost?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.invisalign_cost?.total}
          breakdown={r.invisalign_cost?.breakdown}
          label="Invisalign"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "anesthesia_fee",
    header: "ANESTHESIA",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.anesthesia_fee?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.anesthesia_fee?.total}
          breakdown={r.anesthesia_fee?.breakdown}
          label="Anesthesia"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "anesthesiologist_fee",
    header: "ANESTHESIOLOGIST",
    width: 110,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.anesthesiologist_fee?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.anesthesiologist_fee?.total}
          breakdown={r.anesthesiologist_fee?.breakdown}
          label="Anesthesiologist"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "facility_fee",
    header: "FACILITY",
    width: 80,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.facility_fee?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.facility_fee?.total}
          breakdown={r.facility_fee?.breakdown}
          label="Facility"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "botox",
    header: "BOTOX",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.botox?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.botox?.total}
          breakdown={r.botox?.breakdown}
          label="Botox"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "production_adjustment",
    header: "PRODUCTION ADJ.",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.production_adjustment?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.production_adjustment?.total}
          breakdown={r.production_adjustment?.breakdown}
          label="Production Adj."
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "collection_adjustment",
    header: "COLLECTION ADJ.",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.collection_adjustment?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.collection_adjustment?.total}
          breakdown={r.collection_adjustment?.breakdown}
          label="Collection Adj."
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "other_deduction",
    header: "OTHER DEDUCTION",
    width: 110,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) => {
      const tiers = Array.isArray(r.other_deduction) ? r.other_deduction : [];
      if (tiers.length === 0)
        return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      const amountOf = (t) => {
        const explicit = Number(t?.amount);
        if (Number.isFinite(explicit) && explicit !== 0) return explicit;
        if (t?.hour_rate !== undefined || t?.hours !== undefined) {
          return (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
        }
        return Number(t?.profit) || 0;
      };
      const total = tiers.reduce((sum, t) => sum + (Number(amountOf(t)) || 0), 0);
      const title = tiers
        .map((t) => {
          const amt = amountOf(t);
          if (t?.hour_rate !== undefined || t?.hours !== undefined) {
            const hr = Number(t?.hour_rate) || 0;
            const hrs = Number(t?.hours) || 0;
            return `${t?.label || "?"} (${hrs}h × ${formatMoney(hr)}): ${formatMoney(amt)}`;
          }
          const pct = Number(t?.percent) || 0;
          const input = toNum(t?.input_value);
          return `${t?.label || "?"} (${pct}%): ${formatMoney(input)} → ${formatMoney(amt)}`;
        })
        .join("\n");
      return (
        <span className="text-palette-slate-primary cursor-help" title={title}>
          {formatMoney(total)}
        </span>
      );
    },
  },
  {
    key: "insurance",
    header: "INSURANCE",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.insurance) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <span className="text-palette-slate-primary">{formatMoney(r.insurance)}</span>
      ),
  },
  {
    key: "hours_worked",
    header: "HOURS",
    width: 70,
    render: (r) =>
      toNum(r.hours_worked?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.hours_worked?.total}
          breakdown={r.hours_worked?.breakdown}
          label="Hours"
          formatter={(v) => Number(v).toFixed(2)}
        />
      ),
  },
  {
    key: "associate_fee_percentage",
    header: "ASSOCIATE FEE %",
    width: 80,
    headerClassName: FEE_PERCENT_COLORS.header,
    cellClassName: FEE_PERCENT_COLORS.cell,
    render: (r) => {
      if (!hasFee(r) && onUpdateClick) {
        return (
          <Button variant="gray" size="xs" onClick={() => onUpdateClick(r)}>
            Update
          </Button>
        );
      }
      return percent(r.associate_fee_percentage);
    },
  },
  {
    key: "approval",
    header: "APPROVAL BY ADMIN",
    width: 130,
    // Status-only: reflects admin (final) approval. No action button here.
    render: (r) =>
      isApprovalApproved(r.admin_approval) ? (
        <span className="text-palette-green-badge text-base" title="Admin approved">
          ✓
        </span>
      ) : (
        <span className="text-palette-red-indicator text-base" title="Not yet admin-approved">
          ✗
        </span>
      ),
  },
  {
    key: "create_check",
    header: "CHECK",
    width: 110,
    headerClassName: CHECK_HEADER_COLOR,
    // Keep the cell's normal padding so row height matches other columns.
    // Cleared / uncleared backgrounds are painted via a negative-margin
    // "bleed" div inside the cell.
    render: (r) => {
      const vendorKey = r.netsuite_vendor_id != null ? String(r.netsuite_vendor_id) : "";
      const clinicKey = String(r.clinic || "").toUpperCase();
      const lookupKey = vendorKey && clinicKey ? `${vendorKey}::${clinicKey}` : "";
      const existingList = findExistingChecksForRow ? findExistingChecksForRow(r) : [];
      const expectedInfo = vendorKey ? expectedClearInfoByVendor?.[vendorKey] : null;
      const expectedClearText = expectedInfo?.label || null;
      const expectedClearedDates = Array.isArray(expectedInfo?.clearedDates)
        ? expectedInfo.clearedDates
        : [];

      const expectedMeta =
        vendorKey && expectedClearLoading && !expectedInfo ? (
          <span className="text-[11px] text-palette-slate-secondary italic">
            Expected clear: loading...
          </span>
        ) : expectedClearText ? (
          <ExpectedClearHover label={expectedClearText} clearedDates={expectedClearedDates} />
        ) : null;

      const withExpected = (content) =>
        expectedMeta ? (
          <div className="flex flex-col gap-1">
            {content}
            {expectedMeta}
          </div>
        ) : (
          content
        );

      // Paint full-cell background by bleeding into the td's padding via
      // negative margins, then re-applying the same padding inside.
      const bleed = (bg, content) =>
        bg ? <div className={`-mx-3 -my-2 px-3 py-2 ${bg}`}>{content}</div> : content;
      if (lookupKey && justCreatedKeys?.has?.(lookupKey) && existingList.length === 0) {
        return bleed(
          "bg-palette-emerald-soft",
          withExpected(
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-palette-emerald-hover">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Done!!
            </span>,
          ),
        );
      }
      if (existingList.length > 0) {
        const anyUncleared = existingList.some((c) => !c.cleared);
        // Two states only: any check still uncleared paints the cell amber to draw
        // attention, otherwise emerald. Clearing before day 15 no longer paints the
        // whole cell rose — the money did clear, so the cell reads as success and
        // the early clear is called out by the rose "Cleared before 15" note beside
        // the badge instead.
        const cellBg = anyUncleared ? "bg-palette-amber-soft" : "bg-palette-emerald-soft";
        return bleed(
          cellBg,
          withExpected(
            <div className="flex flex-col gap-1 leading-tight">
              {existingList.map((existing, idx) => {
                const label = existing.transactionnumber ?? existing.tranid ?? "-";
                const cleared = !!existing.cleared;
                const pre15Cleared =
                  cleared && isClearedBefore15th(existing.cleareddate || existing.trandate);
                // A pre-15 clear is styled exactly like any other cleared check —
                // green. The only difference is the note rendered beside the badge.
                const textBase = cleared
                  ? "text-palette-emerald-text"
                  : "text-palette-amber-primary";
                const linkText = cleared
                  ? "text-palette-emerald-hover hover:text-palette-emerald-primary"
                  : "text-palette-amber-heading hover:text-palette-amber-primary";
                const badge = (
                  <span
                    className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[10px] font-semibold ring-1 ring-inset ${
                      cleared
                        ? "bg-palette-emerald-muted text-palette-emerald-hover ring-palette-emerald-border"
                        : "bg-palette-amber-muted text-palette-amber-heading ring-palette-amber-border"
                    }`}
                    title={
                      pre15Cleared
                        ? "Cleared in NetSuite, before day 15"
                        : cleared
                          ? "Cleared in NetSuite"
                          : "Not yet cleared"
                    }
                  >
                    <svg
                      className="w-2.5 h-2.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {cleared ? (
                        <path d="M5 13l4 4L19 7" />
                      ) : (
                        <circle cx="12" cy="12" r="9" strokeWidth="2" />
                      )}
                    </svg>
                    {cleared ? "Cleared" : "Uncleared"}
                    {cleared && existing.cleareddate ? ` ${existing.cleareddate}` : ""}
                  </span>
                );
                return (
                  <div
                    key={existing.transactionnumber ?? `${existing.trandate}-${idx}`}
                    className={`flex flex-col ${textBase} ${
                      idx > 0 ? "border-t border-palette-emerald-border/60 pt-1" : ""
                    }`}
                  >
                    <span className="text-[11px] font-semibold">
                      {existing.trandate}
                      {existing.amount ? (
                        <span className="ml-1 font-normal opacity-80">
                          {formatMoney(existing.amount)}
                        </span>
                      ) : null}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {existing.link ? (
                        <a
                          href={existing.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs underline ${linkText}`}
                          title="Open in NetSuite"
                        >
                          #{label}
                        </a>
                      ) : (
                        <span className="text-xs">#{label}</span>
                      )}
                      {badge}
                      {/* Pre-15 note, in rose so it stands out: the check DID clear
                          (hence the emerald cell and badge), but it cleared earlier
                          in the month than the 15th payout date — which is the thing
                          the "Cleared <15" filter above exists to hunt for. */}
                      {pre15Cleared ? (
                        <span
                          className="inline-flex items-center whitespace-nowrap rounded-full bg-palette-rose-muted px-1.5 py-[1px] text-[10px] font-semibold text-palette-rose-heading ring-1 ring-inset ring-palette-rose-border"
                          title="This check cleared before day 15 of the month"
                        >
                          Cleared before 15
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>,
          ),
        );
      }
      if (!isApprovalApproved(r.admin_approval)) {
        return withExpected(
          <span className="text-palette-gray-placeholder text-xs italic">Approve first</span>,
        );
      }
      const { ok } = validateCheckData({ row: r });
      if (!ok) {
        return withExpected(
          <span className="text-palette-gray-placeholder text-xs italic">Not eligible</span>,
        );
      }
      if (!onCreateCheck) return null;
      return withExpected(
        <Button
          variant="primary"
          size="xs"
          onClick={() => onCreateCheck(r)}
          title="Create a NetSuite check for this practitioner"
        >
          Create
        </Button>,
      );
    },
  },
];
